import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Lock, CheckCircle2, AlertCircle, Check, Zap, QrCode, CreditCard, ShieldCheck } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

export default function CheckoutModal({ product, profile, onClose }) {
  const [loading, setLoading] = useState(false);
  const isPhysical = product.requires_shipping === true || String(product.requires_shipping) === 'true';
  const initialStep = isPhysical ? -0.75 : (product.is_ai ? 0 : -0.75);
  const [step, setStep] = useState(initialStep);
  const [error, setError] = useState('');
  const [finalOrderData, setFinalOrderData] = useState(null);
  
  // AI Personalization State
  const aiVariablesList = product.is_ai && product.ai_prompt ? Array.from(product.ai_prompt.matchAll(/\[(.*?)\]/g)).map(m => m[1]) : [];
  const [aiAnswers, setAiAnswers] = useState({});
  const [buyerEmail, setBuyerEmail] = useState('');
  const [shippingName, setShippingName] = useState('');
  const [shippingPhoneCode, setShippingPhoneCode] = useState('+1');
  const [shippingPhoneNumber, setShippingPhoneNumber] = useState('');
  const [shippingAddressLine1, setShippingAddressLine1] = useState('');
  const [shippingAddressLine2, setShippingAddressLine2] = useState('');
  const [shippingCity, setShippingCity] = useState('');
  const [shippingState, setShippingState] = useState('');
  const [shippingPostalCode, setShippingPostalCode] = useState('');
  const [shippingCountry, setShippingCountry] = useState('US');

  const compiledShippingAddress = isPhysical 
    ? `${shippingAddressLine1}, ${shippingAddressLine2 ? shippingAddressLine2 + ', ' : ''}${shippingCity}, ${shippingState} ${shippingPostalCode}, ${shippingCountry}`
    : '';
  const compiledShippingPhone = isPhysical
    ? `${shippingPhoneCode} ${shippingPhoneNumber}`
    : '';
  const [utrNumber, setUtrNumber] = useState('');

  const razorpayKey = import.meta.env.VITE_RAZORPAY_KEY_ID;
  const upiId = profile?.tip_jar?.upi_id || profile?.upi_id || ''; // Empty if not set

  const initDirectUpi = async () => {
    setStep(1.5);
    const amount = product?.social_data?.price || product?.price || '0.00';
    try {
      await fetch('/api/v1/direct-upi-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profile_id: profile.id,
          product_name: product.is_ai ? JSON.stringify({ name: product.title || product.name, answers: aiAnswers }) : (product.title || product.name),
          buyer_email: buyerEmail || 'guest@example.com',
          shipping_name: shippingName || '',
          shipping_phone: compiledShippingPhone,
          shipping_address: compiledShippingAddress,
          amount: amount
        })
      });
    } catch (e) {
      console.error("Failed to create pending order", e);
    }
  };

  const proceedToPaymentSelection = () => {
    const hasRazorpay = Boolean(profile?.tip_jar?.razorpay_key_id);
    if (hasRazorpay && upiId) {
      setStep(-0.5); // Show Selection Screen
    } else if (upiId && !hasRazorpay) {
      initDirectUpi(); // Direct to UPI
    } else {
      initRazorpay(); // Direct to Razorpay
    }
  };

  const initRazorpay = async () => {
    setStep(1);
    setLoading(true);
    const loadScript = () => {
      return new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
      });
    };

    const res = await loadScript();
    if (!res) {
      setError('Failed to load Razorpay SDK. Are you offline?');
      setLoading(false);
      return;
    }

    try {
      const idempotencyKey = crypto.randomUUID();
      const orderRes = await fetch('/api/v1/razorpay-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profile_id: profile.id,
          product_name: product.is_ai ? JSON.stringify({ name: product.title || product.name, answers: aiAnswers }) : (product.title || product.name),
          idempotency_key: idempotencyKey,
          buyer_email: buyerEmail || 'buyer@example.com',
          shipping_name: shippingName || '',
          shipping_phone: compiledShippingPhone,
          shipping_address: compiledShippingAddress
        })
      });

      const orderData = await orderRes.json();
      setFinalOrderData(orderData);
      
      if (!orderRes.ok) {
        throw new Error(orderData.error || 'Failed to generate order.');
      }

      const options = {
        key: orderData.key_id || razorpayKey,
        amount: orderData.amount,
        currency: orderData.currency,
        name: profile?.name || 'Creator Hub',
        description: product.name || product.title,
        image: profile?.avatar_url,
        order_id: orderData.id,
        handler: async function (response) {
          try {
            const verifyRes = await fetch('/api/v1/razorpay-verify', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                profile_id: profile?.id
              })
            });
            if (!verifyRes.ok) throw new Error('Verification failed');
            setLoading(false);
            setStep(2);
          } catch (err) {
            setError('Payment verification failed. Please contact support.');
            setLoading(false);
          }
        },
        prefill: { email: buyerEmail },
        theme: { color: profile?.button_color || '#000000' },
        modal: { ondismiss: () => onClose() }
      };

      const rzp1 = new window.Razorpay(options);
      rzp1.on('payment.failed', function (response) {
        setError(response.error.description);
        setLoading(false);
      });
      rzp1.open();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleUtrSubmit = (e) => {
    e.preventDefault();
    if (utrNumber.length < 12) {
      setError('Please enter a valid 12-digit UTR number.');
      return;
    }
    setStep(2); // In reality, we'd fire an API call here to record the manual UTR for creator approval
  };

  // --- SHIPPING DETAILS SCREEN ---
  if (step === -0.75) {
    const isPhysical = product.requires_shipping === true || String(product.requires_shipping) === 'true';
    if (!isPhysical) {
      return (
        <AnimatePresence>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[600] flex flex-col items-center justify-end sm:justify-center p-0 sm:p-6" style={{ backdropFilter: 'blur(10px)', background: 'rgba(0,0,0,0.6)' }}>
            <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl p-6 sm:p-8 space-y-5">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-black text-slate-900 tracking-tight flex items-center gap-2">
                  <ShieldCheck className="text-emerald-500" size={20} /> Contact Info ({String(product.requires_shipping)})
                </h3>
                <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-900 bg-slate-100 rounded-full transition-colors"><X size={16}/></button>
              </div>
              <p className="text-sm font-medium text-slate-500 leading-relaxed">
                Where should we send your receipt and digital product?
              </p>
              <form onSubmit={(e) => { e.preventDefault(); if (product.is_ai) setStep(0); else proceedToPaymentSelection(); }} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">Email</label>
                  <input required type="email" value={buyerEmail} onChange={e => setBuyerEmail(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-slate-900 transition-colors text-sm font-bold text-slate-900" placeholder="you@example.com" />
                </div>
                <button type="submit" className="w-full mt-4 py-3.5 bg-slate-900 text-white font-black uppercase tracking-widest text-xs rounded-xl shadow-[4px_4px_0px_0px_rgba(0,0,0,0.1)] active:shadow-none active:translate-y-1 active:translate-x-1 transition-all">
                  {product.is_ai ? 'Continue' : 'Continue to Payment'}
                </button>
              </form>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      );
    } else {
      return (
        <AnimatePresence>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[600] flex flex-col items-center justify-end sm:justify-center p-0 sm:p-6" style={{ backdropFilter: 'blur(10px)', background: 'rgba(0,0,0,0.6)' }}>
            <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl p-6 sm:p-8 space-y-5 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center sticky top-0 bg-white z-10 pb-2">
                <h3 className="text-xl font-black text-slate-900 tracking-tight flex items-center gap-2">
                  <ShieldCheck className="text-emerald-500" size={20} /> Shipping Details
                </h3>
                <button type="button" onClick={onClose} className="p-2 text-slate-400 hover:text-slate-900 bg-slate-100 rounded-full transition-colors"><X size={16}/></button>
              </div>
              <p className="text-sm font-medium text-slate-500 leading-relaxed">
                Please provide your full shipping address for physical delivery.
              </p>
              <form onSubmit={(e) => { e.preventDefault(); if (product.is_ai) setStep(0); else proceedToPaymentSelection(); }} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">Email</label>
                  <input required type="email" value={buyerEmail} onChange={e => setBuyerEmail(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-slate-900 transition-colors text-sm font-bold text-slate-900" placeholder="you@example.com" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">Full Name</label>
                  <input required type="text" value={shippingName} onChange={e => setShippingName(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-slate-900 transition-colors text-sm font-bold text-slate-900" placeholder="John Doe" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">Phone Number</label>
                  <div className="flex gap-2">
                    <select value={shippingPhoneCode} onChange={e => setShippingPhoneCode(e.target.value)} className="w-24 px-3 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-slate-900 transition-colors text-sm font-bold text-slate-900 appearance-none text-center">
                      <option value="+1">+1 (US/CA)</option>
                      <option value="+44">+44 (UK)</option>
                      <option value="+91">+91 (IN)</option>
                      <option value="+61">+61 (AU)</option>
                      <option value="+49">+49 (DE)</option>
                      <option value="+33">+33 (FR)</option>
                      <option value="+81">+81 (JP)</option>
                      <option value="+86">+86 (CN)</option>
                    </select>
                    <input required type="tel" value={shippingPhoneNumber} onChange={e => setShippingPhoneNumber(e.target.value)} className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-slate-900 transition-colors text-sm font-bold text-slate-900" placeholder="234 567 8900" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">Address Line 1</label>
                  <input required type="text" value={shippingAddressLine1} onChange={e => setShippingAddressLine1(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-slate-900 transition-colors text-sm font-bold text-slate-900" placeholder="123 Main St" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">Address Line 2 (Optional)</label>
                  <input type="text" value={shippingAddressLine2} onChange={e => setShippingAddressLine2(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-slate-900 transition-colors text-sm font-bold text-slate-900" placeholder="Apt 4B" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">City</label>
                    <input required type="text" value={shippingCity} onChange={e => setShippingCity(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-slate-900 transition-colors text-sm font-bold text-slate-900" placeholder="New York" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">State / Province</label>
                    <input required type="text" value={shippingState} onChange={e => setShippingState(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-slate-900 transition-colors text-sm font-bold text-slate-900" placeholder="NY" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">ZIP / Postal Code</label>
                    <input required type="text" value={shippingPostalCode} onChange={e => setShippingPostalCode(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-slate-900 transition-colors text-sm font-bold text-slate-900" placeholder="10001" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">Country</label>
                    <select value={shippingCountry} onChange={e => setShippingCountry(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-slate-900 transition-colors text-sm font-bold text-slate-900 appearance-none">
                      <option value="US">United States</option>
                      <option value="GB">United Kingdom</option>
                      <option value="IN">India</option>
                      <option value="CA">Canada</option>
                      <option value="AU">Australia</option>
                      <option value="DE">Germany</option>
                      <option value="FR">France</option>
                    </select>
                  </div>
                </div>
                <button type="submit" className="w-full mt-4 py-3.5 bg-slate-900 text-white font-black uppercase tracking-widest text-xs rounded-xl shadow-[4px_4px_0px_0px_rgba(0,0,0,0.1)] active:shadow-none active:translate-y-1 active:translate-x-1 transition-all">
                  {product.is_ai ? 'Continue' : 'Continue to Payment'}
                </button>
              </form>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      );
    }
  }

  // --- SELECTION SCREEN ---
  if (step === -0.5) {
    return (
      <AnimatePresence>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[600] flex flex-col items-center justify-end sm:justify-center p-0 sm:p-6" style={{ backdropFilter: 'blur(10px)', background: 'rgba(0,0,0,0.6)' }}>
          <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl p-6 sm:p-8 space-y-5">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-black text-slate-900 tracking-tight">Select Payment Method</h3>
              <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-900 bg-slate-100 rounded-full transition-colors"><X size={16}/></button>
            </div>
            <div className="space-y-3">
              <button onClick={() => initDirectUpi()} className="w-full flex items-center justify-between p-4 border-2 border-emerald-500 rounded-2xl bg-emerald-50 hover:bg-emerald-100 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm">
                    <QrCode className="text-emerald-500" size={20} />
                  </div>
                  <div className="text-left">
                    <div className="font-black text-slate-900">Direct UPI (QR Code)</div>
                    <div className="text-xs font-bold text-emerald-600">0% Platform Fee · Instant</div>
                  </div>
                </div>
              </button>
              <button onClick={() => initRazorpay()} className="w-full flex items-center justify-between p-4 border-2 border-slate-200 rounded-2xl hover:border-slate-900 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                    <CreditCard className="text-slate-900" size={20} />
                  </div>
                  <div className="text-left">
                    <div className="font-black text-slate-900">Cards, Apple Pay, GPay</div>
                    <div className="text-xs font-bold text-slate-500">Standard Gateway Fees Apply</div>
                  </div>
                </div>
              </button>
            </div>
            <div className="text-[10px] text-center font-black uppercase tracking-widest text-slate-400 flex items-center justify-center gap-1">
              <ShieldCheck size={12} /> Decentralized Payments Engine
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // --- AI PERSONALIZATION SCREEN ---
  if (step === 0) {
    return (
      <AnimatePresence>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[600] flex flex-col items-center justify-end sm:justify-center p-0 sm:p-6" style={{ backdropFilter: 'blur(10px)', background: 'rgba(0,0,0,0.6)' }}>
          <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl p-6 sm:p-8 space-y-5">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-black text-slate-900 tracking-tight flex items-center gap-2">
                <Zap className="text-emerald-500" size={20} /> Personalization
              </h3>
              <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-900 bg-slate-100 rounded-full transition-colors"><X size={16}/></button>
            </div>
            <p className="text-sm font-medium text-slate-500 leading-relaxed">
              This digital product is synthesized instantly by AI. Please provide your details below.
            </p>
            <form onSubmit={(e) => { e.preventDefault(); proceedToPaymentSelection(); }} className="space-y-4">
              {!isPhysical && (
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">Email (for receipt)</label>
                  <input required type="email" value={buyerEmail} onChange={e => setBuyerEmail(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-slate-900 transition-colors text-sm font-bold text-slate-900" placeholder="you@example.com" />
                </div>
              )}
              {aiVariablesList.map(v => (
                <div key={v} className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">{v}</label>
                  <input required type="text" value={aiAnswers[v] || ''} onChange={e => setAiAnswers({...aiAnswers, [v]: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-slate-900 transition-colors text-sm font-bold text-slate-900" placeholder={`Enter ${v}`} />
                </div>
              ))}
              <button type="submit" className="w-full mt-4 py-3.5 bg-slate-900 text-white font-black uppercase tracking-widest text-xs rounded-xl shadow-[4px_4px_0px_0px_rgba(0,0,0,0.1)] active:shadow-none active:translate-y-1 active:translate-x-1 transition-all">
                Proceed to Checkout
              </button>
            </form>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // --- DIRECT UPI QR SCREEN ---
  if (step === 1.5) {
    const amount = product?.social_data?.price || product?.price || '0.00';
    const upiString = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(profile?.name || 'Creator')}&am=${amount}&cu=INR`;
    
    

    return (
      <AnimatePresence>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[600] flex flex-col items-center justify-end sm:justify-center p-0 sm:p-6" style={{ backdropFilter: 'blur(10px)', background: 'rgba(0,0,0,0.6)' }}>
          <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl p-6 sm:p-8 text-center space-y-5">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-black text-slate-900 tracking-tight flex items-center gap-2">
                <QrCode className="text-emerald-500" size={20} /> Direct UPI Transfer
              </h3>
              <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-900 bg-slate-100 rounded-full transition-colors"><X size={16}/></button>
            </div>
            
            <div className="p-6 bg-slate-50 rounded-3xl border-2 border-slate-100 flex flex-col items-center justify-center space-y-4">
              <div className="bg-white p-3 rounded-2xl shadow-sm border border-slate-200">
                <QRCodeSVG value={upiString} size={180} level="H" />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-slate-400">Scan & Pay</p>
                <p className="text-2xl font-black text-slate-900">₹{amount}</p>
                <p className="text-sm font-bold text-slate-500 mt-1">To: {upiId}</p>
              </div>
            </div>

            <div className="space-y-4 text-center mt-6">
              <p className="text-sm font-bold text-slate-600">
                After your payment is complete, proceed to the Buyer Portal to track your order.
              </p>
              <a 
                href="/portal"
                className="block w-full py-4 bg-emerald-500 text-white font-black uppercase tracking-widest text-sm rounded-xl shadow-[4px_4px_0px_0px_rgba(16,185,129,0.2)] active:shadow-none active:translate-y-1 active:translate-x-1 transition-all"
              >
                Go to Buyer Portal
              </a>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // --- SUCCESS SCREEN ---
  if (step === 2) {
    return (
      <AnimatePresence>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[600] flex flex-col items-center justify-end sm:justify-center p-0 sm:p-6" style={{ backdropFilter: 'blur(10px)', background: 'rgba(0,0,0,0.6)' }}>
          <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl p-8 sm:p-12 text-center space-y-4 relative">
            <div className="w-20 h-20 bg-emerald-100 text-emerald-500 rounded-full mx-auto flex items-center justify-center">
              <Check size={40} strokeWidth={3} />
            </div>
            <h3 className="text-2xl font-black text-slate-900">Payment Successful</h3>
            <p className="text-slate-500 font-medium">Your purchase is confirmed.</p>
            <a href={`/portal?order_id=${finalOrderData?.id || utrNumber}`} className="mt-4 block w-full px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl transition-colors">
              Open Buyer Portal
            </a>
            <button onClick={onClose} className="mt-2 px-6 py-3 w-full bg-slate-100 hover:bg-slate-200 text-slate-900 font-bold rounded-xl transition-colors">
              Close
            </button>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // --- LOADING SCREEN ---
  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[600] flex flex-col items-center justify-center p-6" style={{ backdropFilter: 'blur(10px)', background: 'rgba(0,0,0,0.6)' }}>
        <div className="bg-white p-8 rounded-3xl shadow-2xl max-w-sm w-full text-center space-y-4 relative">
          <button onClick={onClose} className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-900 bg-slate-100 rounded-full transition-colors"><X size={16}/></button>
          {error ? (
            <>
              <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full mx-auto flex items-center justify-center mb-2"><AlertCircle size={32} /></div>
              <h3 className="font-bold text-slate-900">Checkout Error</h3>
              <p className="text-sm text-slate-500">{error}</p>
            </>
          ) : (
            <>
              <div className="w-12 h-12 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin mx-auto"></div>
              <p className="font-bold text-slate-900 uppercase tracking-widest text-sm">Initializing Secure Checkout...</p>
              <p className="text-xs text-slate-500 flex justify-center items-center gap-1"><Lock size={10} /> Decentralized Payments</p>
            </>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
